import { getRedirectUrls } from 'utils/platform';
import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';
import { awsConfig, env } from 'core/config';
import { AWS_REGION, bucketNames } from './constants';

const { redirectSignIn, redirectSignOut } = getRedirectUrls(awsConfig);

interface BucketInfo {
  bucketName: string;
  region: string;
  paths?: Record<string, Record<string, string[] | undefined>>;
}

const buckets = bucketNames.reduce((acc, bucket) => {
  acc[bucket] = {
    bucketName: bucket,
    region: AWS_REGION
  };
  return acc;
}, {} as Record<string, BucketInfo>);

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: awsConfig.aws_user_pools_id,
      userPoolClientId: awsConfig.aws_user_pools_web_client_id,
      identityPoolId: awsConfig.aws_cognito_identity_pool_id,
      loginWith: {
        oauth: {
          domain: awsConfig.oauth.domain,
          scopes: awsConfig.oauth.scope ? awsConfig.oauth.scope : [],
          redirectSignIn: redirectSignIn ? [redirectSignIn] : [],
          redirectSignOut: redirectSignOut ? [redirectSignOut] : [],
          responseType: awsConfig.oauth.responseType,
          providers:
            awsConfig.aws_cognito_social_providers?.map((provider: string) =>
              provider.toLowerCase()
            ) || []
        },
        username: true,
        email: true
      }
    }
  },
  API: {
    GraphQL: {
      endpoint: env.GRAPHQL_ENDPOINT,
      region: AWS_REGION,
      defaultAuthMode: 'userPool'
    },
    Events: {
      endpoint: env.EVENTS_ENDPOINT,
      region: AWS_REGION,
      defaultAuthMode: 'userPool'
    }
  },
  Storage: {
    S3: {
      region: AWS_REGION,
      buckets
    }
  }
});

// Detect if Popp is embedded in an iframe (e.g. inside the Tux app on a different domain).
// try/catch guards against SecurityError in sandboxed iframes without allow-same-origin.
const isEmbedded = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

// Use CookieStorage for auth tokens so they're shared across subdomains
// (e.g. app.popp.com and admin.popp.com) via a common COOKIE_DOMAIN.
// Only enable when:
//   1. The current hostname is within the configured cookie domain
//   2. The app is NOT embedded in an iframe (cookies break cross-origin in iframes;
//      localStorage is same-origin and works reliably)
const hostname = window.location.hostname;
const cookieDomain = env.COOKIE_DOMAIN;
const hostnameMatchesCookieDomain =
  cookieDomain &&
  hostname !== 'localhost' &&
  (hostname === cookieDomain || hostname.endsWith(`.${cookieDomain}`));

if (hostnameMatchesCookieDomain && !isEmbedded) {
  cognitoUserPoolsTokenProvider.setKeyValueStorage(
    new CookieStorage({
      domain: cookieDomain,
      path: '/',
      sameSite: 'lax',
      secure: true
    })
  );
}
